import { FrontendMaterialLimit } from "components/common/forms/MaterialLimitsEditor";
import {
  BasketRead,
  MaterialLimit,
  MaterialLimitBounds,
} from "src/store/api/generatedApi";
import { isLimitsEmpty, Limits } from "src/utils/addNewLimit";
import { typeSafeObjectEntries } from "src/utils/typeSafeObjectEntries";
import { typeSafeObjectFromEntries } from "src/utils/typeSafeObjectFromEntries";

export const createInitialMaterialLimit = (
  baskets: BasketRead[]
): FrontendMaterialLimit => ({
  name: "",
  baskets: typeSafeObjectFromEntries(
    baskets.map((basket) => [
      basket.id,
      {
        basketId: basket.id,
        number: basket.basket_number,
        volume: basket.basket_volume,
        limits: [null, null, null, null],
      },
    ])
  ),
  hardness: 1,
  coefficients: {},
  mixLimits: [null, null, null, null],
});

export const getNewMaterialLimit = (
  frontendMaterialLimit: FrontendMaterialLimit,
  chefGroupId: number
): MaterialLimit => {
  const {
    name,
    hardness,
    mixLimits,
    coefficients,
    baskets: basketsMap,
  } = frontendMaterialLimit;
  const coefficientList = typeSafeObjectEntries(coefficients).map(
    ([material_constraint_class_id, coefficient]) => ({
      // Object keys are always strings at runtime despite the Record<number, _> type,
      // so coerce to number to match the API schema after a JSON round-trip.
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-conversion
      material_constraint_class_id: Number(material_constraint_class_id),
      coefficient,
    })
  );
  const bounds: MaterialLimitBounds[] = [
    ...(mixLimits && !isLimitsEmpty(mixLimits)
      ? [
          {
            basket_id: null,
            min_mass: mixLimits[0],
            soft_min_mass: mixLimits[1],
            soft_max_mass: mixLimits[2],
            max_mass: mixLimits[3],
          },
        ]
      : []),
    ...typeSafeObjectEntries(basketsMap)
      .map(([, basket]): MaterialLimitBounds | null => {
        if (isLimitsEmpty(basket.limits)) {
          return null;
        }
        const [minMass, softMinMass, softMaxMass, maxMass] = basket.limits;
        return {
          basket_id: basket.basketId,
          min_mass: minMass,
          soft_min_mass: softMinMass,
          soft_max_mass: softMaxMass,
          max_mass: maxMass,
        };
      })
      .filter((bound): bound is MaterialLimitBounds => bound !== null),
  ];
  return {
    chef_group_id: chefGroupId,
    name,
    hardness,
    coefficients: coefficientList,
    bounds,
  };
};

export const materialLimitToFrontend = (
  materialLimit: MaterialLimit,
  basketList: BasketRead[]
): FrontendMaterialLimit => {
  const mixBound = materialLimit.bounds.find(
    (bound) => bound.basket_id == null
  );
  const basketBoundsById = Object.fromEntries(
    materialLimit.bounds
      .filter((bound) => bound.basket_id !== null)
      .map((bound) => [bound.basket_id ?? 0, bound])
  );

  return {
    name: materialLimit.name ?? "",
    hardness: materialLimit.hardness,
    coefficients: typeSafeObjectFromEntries(
      materialLimit.coefficients.map(
        ({ material_constraint_class_id, coefficient }) => [
          material_constraint_class_id,
          coefficient,
        ]
      )
    ),
    mixLimits: [
      mixBound?.min_mass ?? null,
      mixBound?.soft_min_mass ?? null,
      mixBound?.soft_max_mass ?? null,
      mixBound?.max_mass ?? null,
    ],
    baskets: typeSafeObjectFromEntries(
      basketList.map((basketRead) => {
        const bound = basketBoundsById[basketRead.id];
        return [
          basketRead.id,
          {
            basketId: basketRead.id,
            number: basketRead.basket_number,
            volume: basketRead.basket_volume,
            limits: [
              bound?.min_mass ?? null,
              bound?.soft_min_mass ?? null,
              bound?.soft_max_mass ?? null,
              bound?.max_mass ?? null,
            ] as Limits,
          },
        ];
      })
    ),
  };
};
